import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Popconfirm,
  Card,
  Typography,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';

const { Title } = Typography;

const ChannelsPage = () => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const queryClient = useQueryClient();

  // Fetch channels
  const { data: channels, isLoading } = useQuery(
    'admin-channels',
    async () => {
      const response = await apiClient.get('/api/admin/channels');
      return response.data.data.channels;
    }
  );

  // Create channel mutation
  const createChannelMutation = useMutation(
    async (channelData) => {
      const response = await apiClient.post('/api/admin/channels', channelData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Channel created successfully');
        queryClient.invalidateQueries('admin-channels');
        setIsModalVisible(false);
        form.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to create channel');
      },
    }
  );

  // Update channel mutation
  const updateChannelMutation = useMutation(
    async ({ id, ...channelData }) => {
      const response = await apiClient.put(`/api/admin/channels/${id}`, channelData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Channel updated successfully');
        queryClient.invalidateQueries('admin-channels');
        setIsModalVisible(false);
        setEditingChannel(null);
        form.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update channel');
      },
    }
  );

  // Delete channel mutation
  const deleteChannelMutation = useMutation(
    async (id) => {
      const response = await apiClient.delete(`/api/admin/channels/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Channel deleted successfully');
        queryClient.invalidateQueries('admin-channels');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to delete channel');
      },
    }
  );

  const handleCreate = () => {
    setEditingChannel(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (channel) => {
    setEditingChannel(channel);
    form.setFieldsValue(channel);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    deleteChannelMutation.mutate(id);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingChannel) {
        updateChannelMutation.mutate({ id: editingChannel.id, ...values });
      } else {
        createChannelMutation.mutate(values);
      }
    } catch (error) {
      // Silently handle form validation error
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this channel?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Title level={2} style={{ margin: 0 }}>Channels Management</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add Channel
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={channels}
          loading={isLoading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} channels`,
          }}
        />
      </Card>

      <Modal
        title={editingChannel ? 'Edit Channel' : 'Add Channel'}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingChannel(null);
          form.resetFields();
        }}
        confirmLoading={createChannelMutation.isLoading || updateChannelMutation.isLoading}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="name"
            label="Channel Name"
            rules={[
              { required: true, message: 'Please enter channel name' },
              { max: 100, message: 'Name must be less than 100 characters' },
            ]}
          >
            <Input placeholder="Enter channel name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { max: 500, message: 'Description must be less than 500 characters' },
            ]}
          >
            <Input.TextArea
              placeholder="Enter channel description"
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ChannelsPage;
